import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  LocalShipping as LocalShippingIcon,
  Edit as EditIcon
} from '@mui/icons-material';

const ViewInvoiceModal = ({ 
  open, 
  onClose, 
  invoice,
  onUpdatePayment,
  onEdit
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  if (!invoice) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/D';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'partial': return 'warning';
      case 'pending': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 'paid': return 'Pagata';
      case 'partial': return 'Parziale';
      case 'pending': return 'Da Pagare';
      default: return status;
    }
  };

  // Parse XML content if available for additional details
  const xmlData = invoice.xml_content ? (() => {
    try {
      return JSON.parse(invoice.xml_content);
    } catch {
      return null;
    }
  })() : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon color="primary" />
            <span>Fattura {invoice.invoice_number}</span>
          </Box>
          <Chip 
            label={getPaymentStatusLabel(invoice.payment_status)}
            color={getPaymentStatusColor(invoice.payment_status)}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Intestazione */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">Numero Fattura</Typography>
                  <Typography variant="h6">{invoice.invoice_number}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">Data Fattura</Typography>
                  <Typography variant="h6">{formatDate(invoice.invoice_date)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">Data Scadenza</Typography>
                  <Typography variant="h6" color={new Date(invoice.due_date) < new Date() && invoice.payment_status !== 'paid' ? 'error' : 'inherit'}>
                    {formatDate(invoice.due_date)}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">File XML</Typography>
                  <Typography variant="body2">{invoice.xml_file_name || 'N/D'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Cedente/Prestatore */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BusinessIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="primary">Cedente/Prestatore (Fornitore)</Typography>
              </Box>
              <Typography fontWeight="bold" gutterBottom>
                {invoice.cedente_denominazione || invoice.supplier?.company_name || 'N/D'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                P.IVA: {invoice.cedente_partita_iva || invoice.supplier?.vat_number || 'N/D'}
              </Typography>
              {invoice.cedente_indirizzo && (
                <Typography variant="body2" color="text.secondary">
                  {invoice.cedente_indirizzo}
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Cessionario/Committente */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BusinessIcon color="secondary" fontSize="small" />
                <Typography variant="subtitle2" color="secondary">Cessionario/Committente</Typography>
              </Box>
              <Typography fontWeight="bold" gutterBottom>
                {invoice.cessionario_denominazione || 'N/D'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                P.IVA: {invoice.cessionario_partita_iva || 'N/D'}
              </Typography>
              {invoice.cessionario_indirizzo && (
                <Typography variant="body2" color="text.secondary">
                  {invoice.cessionario_indirizzo}
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Importi */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Riepilogo Importi
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={2}>
                  <Typography variant="body2" color="text.secondary">Quantità GPL</Typography>
                  <Typography variant="h6">
                    {invoice.quantity_liters ? `${parseFloat(invoice.quantity_liters).toLocaleString('it-IT')} L` : 'N/D'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography variant="body2" color="text.secondary">Prezzo Medio/Litro</Typography>
                  <Typography variant="h6" color="info.main">
                    {(() => {
                      // Calcola prezzo medio: imponibile / litri
                      const liters = parseFloat(invoice.quantity_liters) || 0;
                      const taxable = parseFloat(invoice.taxable_amount) || 0;
                      if (liters > 0 && taxable > 0) {
                        return `€ ${(taxable / liters).toFixed(4)}`;
                      }
                      return 'N/D';
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography variant="body2" color="text.secondary">Imponibile</Typography>
                  <Typography variant="h6">{formatCurrency(invoice.taxable_amount)}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography variant="body2" color="text.secondary">IVA {invoice.vat_rate ? `(${invoice.vat_rate}%)` : ''}</Typography>
                  <Typography variant="h6">{formatCurrency(invoice.vat_amount)}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography variant="body2" color="text.secondary">Totale</Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {formatCurrency(invoice.total_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography variant="body2" color="text.secondary">Pagato</Typography>
                  <Typography variant="h6" color={invoice.payment_status === 'paid' ? 'success.main' : 'warning.main'}>
                    {formatCurrency(invoice.paid_amount)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Consegne Associate */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocalShippingIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="primary">
                  Consegne Associate ({invoice.deliveries?.length || 0})
                </Typography>
              </Box>
              
              {invoice.deliveries && invoice.deliveries.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>DDT</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell>Impianto</TableCell>
                        <TableCell align="right">Quantità (L)</TableCell>
                        <TableCell align="right">€/L</TableCell>
                        <TableCell align="right">Imponibile</TableCell>
                        <TableCell align="right">IVA</TableCell>
                        <TableCell align="right">Totale</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.deliveries.map((delivery) => (
                        <TableRow key={delivery.id}>
                          <TableCell>{delivery.ddt_number || 'N/D'}</TableCell>
                          <TableCell>{formatDate(delivery.delivery_date)}</TableCell>
                          <TableCell>
                            {delivery.plant_code || delivery.contract?.cod_con || 'N/D'}
                          </TableCell>
                          <TableCell align="right">
                            {parseFloat(delivery.quantity_liters || 0).toLocaleString('it-IT')}
                          </TableCell>
                          <TableCell align="right">
                            {delivery.price_per_liter ? formatCurrency(delivery.price_per_liter) : 'N/D'}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(delivery.taxable_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(delivery.vat_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(delivery.total_amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  Nessuna consegna associata a questa fattura
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Stato Pagamento */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PaymentIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" color="primary">Stato Pagamento</Typography>
              </Box>
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Stato</Typography>
                  <Chip 
                    label={getPaymentStatusLabel(invoice.payment_status)}
                    color={getPaymentStatusColor(invoice.payment_status)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Importo Dovuto</Typography>
                  <Typography variant="h6">{formatCurrency(invoice.total_amount)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Importo Pagato</Typography>
                  <Typography variant="h6" color="success.main">
                    {formatCurrency(invoice.paid_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">Residuo</Typography>
                  <Typography variant="h6" color={invoice.payment_status !== 'paid' ? 'error.main' : 'inherit'}>
                    {formatCurrency((invoice.total_amount || 0) - (invoice.paid_amount || 0))}
                  </Typography>
                </Grid>
              </Grid>

              {invoice.payment_status !== 'paid' && onUpdatePayment && (
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Button 
                    variant="contained" 
                    color="success"
                    onClick={() => onUpdatePayment(invoice)}
                    startIcon={<PaymentIcon />}
                  >
                    Registra Pagamento
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Note */}
          {invoice.notes && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>Note</Typography>
                <Typography variant="body2">{invoice.notes}</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {onEdit && (
          <Button 
            onClick={() => onEdit(invoice)}
            startIcon={<EditIcon />}
            color="primary"
          >
            Modifica
          </Button>
        )}
        <Button onClick={onClose}>Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewInvoiceModal;
